import React, { useMemo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { TInputState } from 'lib/types/common';
import { IView, IViews, TAlign } from 'lib/types/model';
import { useEffect, useRef, useState } from 'react';
import { useSpring } from '@react-spring/three';
import * as THREE from 'three';
import { TrackballControls } from '@react-three/drei';
import { TrackballControls as TrackballControlsImpl } from 'three-stdlib';

interface CameraControlsProps {
  controlsRef: React.MutableRefObject<TrackballControlsImpl | null>;
  cameraAlign: TInputState<TAlign>;
  isAnimated: TInputState<boolean>;
}

const CameraControls = ({ controlsRef, cameraAlign, isAnimated }: CameraControlsProps) => {
  const { camera } = useThree();
  const views: IViews = {
    front: {
      position: [0, -500, 0],
      up: [0, 0, 1],
    },
    back: {
      position: [0, 500, 0],
      up: [0, 0, 1],
    },
    left: {
      position: [500, 0, 0],
      up: [0, 0, 1],
    },
    right: {
      position: [-500, 0, 0],
      up: [0, 0, 1],
    },
    top: {
      position: [0, 0, 500],
      up: [0, 1, 0],
    },
    bottom: {
      position: [0, 0, -500],
      up: [0, -1, 0],
    },
  };
  const view = useMemo(() => {
    if (!cameraAlign.value) return;

    return views[cameraAlign.value];
  }, [cameraAlign.value]);

  // 짐벌락 수정 코드
  useEffect(() => {
    camera.rotation.order = 'YXZ';
  }, [camera]);

  const [springProps, api] = useSpring(() => ({
    position: [0, 0, 500],
    rotation: [0, 0, 0],
    up: [0, 0, 0],
    onStart: () => {
      isAnimated.setValue(true);
    },
    onRest: () => {
      isAnimated.setValue(false);
      cameraAlign.setValue(null);
    },
    config: { mass: 1, tension: 170, friction: 26 },
    reset: true,
  }));

  useFrame(() => {
    if (!controlsRef.current) return;

    if (isAnimated.value) {
      controlsRef.current?.reset();

      const { position, rotation, up } = springProps;

      const [px, py, pz] = position.get();
      const [rx, ry, rz] = rotation.get();
      const [ux, uy, uz] = up.get();

      camera.up.set(ux, uy, uz);
      camera.position.set(px, py, pz);
      camera.rotation.set(rx, ry, rz);

      camera.lookAt(new THREE.Vector3(0, 0, 0));

      // TrackballControls 업데이트
      if (controlsRef.current) {
        controlsRef.current?.update();
      }
    }
    controlsRef.current?.update();
  });

  useEffect(() => {
    if (!view) return;

    const { x: rx, y: ry, z: rz } = camera.rotation;
    const { x: px, y: py, z: pz } = camera.position;

    api.start({
      from: {
        position: [px, py, pz],
      },
      to: {
        position: view.position,
        rotation: [rx, ry, rz],
        up: view.up,
      },
    });
  }, [view]);

  return (
    <TrackballControls
      ref={controlsRef}
      rotateSpeed={3}
      zoomSpeed={0.3}
      panSpeed={0.05}
      mouseButtons={{
        LEFT: 2,
        MIDDLE: 2,
        RIGHT: 1,
      }}
    />
  );
};

export default CameraControls;
